import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import {
  Truck,
  MapPin,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Calendar,
  IndianRupee,
  ShoppingCart,
  Phone,
} from "lucide-react";

const STATUS = {
  open: { color: "bg-bravura-100 text-bravura-700", icon: AlertCircle, label: "Open" },
  settled: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Settled" },
};

const EXPENSE_LABELS = {
  rto: "RTO",
  tolls: "Tolls",
  dala: "Dala / Loading",
  unloading: "Unloading",
  cash: "Cash",
  other: "Other",
};

export default function TripDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/logistics/trips/by-driver/${id}`);
        setT(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!t) return <div className="text-sm text-red-500">Not found</div>;

  const StatusIcon = STATUS[t.status]?.icon || AlertCircle;
  const expense = t.expense || {};

  return (
    <div className="space-y-4" data-testid="trip-detail-page">
      <Link to="/driver/trips" className="inline-flex items-center gap-1.5 text-xs text-slate-500">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to trips
      </Link>

      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-bravura-200">Total Expense</div>
            <div className="text-3xl font-bold" style={{ fontFamily: "Chivo" }}>
              {formatINR(t.actual_total)}
            </div>
          </div>
          <div className={`px-2.5 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 ${STATUS[t.status]?.color}`}>
            <StatusIcon className="h-3 w-3" />
            {STATUS[t.status]?.label}
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-300 flex flex-wrap items-center gap-2">
          <Truck className="h-3.5 w-3.5" /> {t.truck_no}
          <span>•</span>
          <MapPin className="h-3.5 w-3.5" /> {t.location_name}
          <span>•</span>
          <Calendar className="h-3.5 w-3.5" /> {t.date}
        </div>
      </div>

      {t.expected_amount != null && (
        <div className={`rounded-2xl p-3 text-xs ring-1 ${
          t.variance_flagged ? "bg-red-50 ring-red-200 text-red-700" : "bg-emerald-50 ring-emerald-200 text-emerald-700"
        }`}>
          {t.variance_flagged ? "⚠" : "✓"} Expected ₹{Number(t.expected_amount).toFixed(2)} vs Actual ₹{Number(t.actual_total).toFixed(2)} — Variance ₹{Number(t.variance || 0).toFixed(2)}
        </div>
      )}

      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 space-y-2.5">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
          <IndianRupee className="h-3.5 w-3.5" /> Expense Breakdown
        </div>
        {Object.entries(EXPENSE_LABELS).map(([k, lbl]) => {
          const v = expense[k];
          if (!v) return null;
          return (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{lbl}</span>
              <span className="font-mono font-semibold">₹ {Number(v).toFixed(2)}</span>
            </div>
          );
        })}
        {expense.other_remark && (
          <div className="text-[11px] text-slate-500 italic mt-1">
            Other: {expense.other_remark}
          </div>
        )}
        <hr className="border-slate-100" />
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Total</span>
          <span className="font-bold text-bravura-700">₹ {Number(t.actual_total).toFixed(2)}</span>
        </div>
      </div>

      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Amount to Pay</div>
          <div className="text-base font-bold text-slate-900">{formatINR(t.amount_to_pay)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Paid So Far</div>
          <div className="text-base font-bold text-slate-900">{formatINR(t.amount_paid)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Pending</div>
          <div className={`text-base font-bold ${t.pending > 0 ? "text-bravura-700" : "text-emerald-700"}`}>
            {formatINR(t.pending)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Trip #</div>
          <div className="text-base font-bold text-slate-900">{t.trip_seq}</div>
        </div>
      </div>

      {t.driver_notes && (
        <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-3 text-xs text-slate-700">
          <b>My notes:</b> {t.driver_notes}
        </div>
      )}

      {t.trip_type === "market_return" && t.market_freight && (
        <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-red-50 ring-1 ring-amber-200 p-4 space-y-2"
             data-testid="market-freight-block">
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold flex items-center gap-1.5">
            <ShoppingCart className="h-3 w-3" /> Market Return — back-haul
          </div>
          <div className="text-sm font-bold text-slate-900">
            {t.market_freight.transport_name}
          </div>
          {t.market_freight.transport_person_phone && (
            <a href={`tel:${t.market_freight.transport_person_phone}`}
               className="text-xs text-bravura-700 inline-flex items-center gap-1">
              <Phone className="h-3 w-3" /> {t.market_freight.transport_person_phone}
            </a>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs mt-1">
            <div>Freight: <b>{formatINR(t.market_freight.freight_amount)}</b></div>
            <div>Dala: <b>{formatINR(t.market_freight.dala_charges)}</b></div>
            <div>Builty: <b>{formatINR(t.market_freight.builty_charges)}</b></div>
            <div>Received: <b className="text-emerald-700">{formatINR(t.market_freight.amount_received)}</b></div>
          </div>
        </div>
      )}

      {t.trip_type !== "market_return" && (
        <Link to={`/driver/trips/${t.id}/market-return`}
              data-testid="log-market-return-cta"
              className="block w-full rounded-2xl bg-gradient-to-br from-amber-500 to-red-500 text-white p-4 text-center font-semibold active:scale-[0.99] shadow-md">
          <ShoppingCart className="h-4 w-4 inline mr-2" />
          Log Market Return Trip (back-haul)
        </Link>
      )}

      {t.bill_photos?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Bill / POD Photos
          </div>
          <div className="grid grid-cols-2 gap-2">
            {t.bill_photos.map((p, i) => (
              <a key={i} href={p} target="_blank" rel="noreferrer">
                <img src={p} alt={`bill ${i+1}`} className="aspect-square object-cover rounded-xl ring-1 ring-slate-200" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
